/// <amd-module name="Feed/Legacy/ControllerViews/PackageControllerView" />
import * as React from "react";
import { connect } from "react-redux";

import * as StringUtils from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Action } from "VSS/Reflux/Action";

import {
    IPivotBarAction,
    IPivotBarViewAction,
    PivotBarViewActionType
} from "VSSUI/Components/PivotBar/PivotBarAction.Props";
import { FilterBar, IFilterBarProps } from "VSSUI/FilterBar";
import { Hub, IHub } from "VSSUI/Hub";
import { HubHeader, IHubBreadcrumbItem } from "VSSUI/HubHeader";
import { Link } from "VSSUI/Link";
import { HistoryBehavior, IObservableViewStateUrl } from "VSSUI/Navigation/ViewStateNavigation";
import { IVssHubViewState, VssHubViewState } from "VSSUI/Navigation/VssHubViewState";
import { PickListFilterBarItem } from "VSSUI/PickList";
import { PivotBarItem } from "VSSUI/PivotBar";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Filter, FILTER_CHANGE_EVENT } from "VSSUI/Utilities/Filter";
import { IViewOptionsValues, VIEW_OPTIONS_CHANGE_EVENT } from "VSSUI/Utilities/ViewOptions";
import { IVssIconProps, VssIconType } from "VSSUI/VssIcon";

import { PackageDetailsPivot, PackageFilterBarConstants } from "Feed/Common/Constants/Constants";
import { FeedRole, Package, PackageVersion } from "Feed/Common/Contracts/Feed";
import { UpstreamingBehavior, UpstreamVersionVisibility } from "Feed/Common/Protocols/Shared/Contracts/PackagingShared";
import * as PackageResources from "Feed/Common/Resources";
import { FeedHubActions } from "Feed/Core/Redux/FeedHub/FeedHubActions";
import * as FluxActions from "Feed/Legacy/Actions/Actions";
import { RecycleBinActions } from "Feed/Legacy/Actions/RecycleBinActions";
import { ProtocolCommands } from "Feed/Legacy/Common/ProtocolCommands";
import { OpenConnectToFeedDialog } from "Feed/Legacy/Components/ConnectToFeedButton";
import { CreateBadgePanel } from "Feed/Legacy/Components/CreateBadgePanel";
import { LoadingContainer } from "Feed/Legacy/Components/LoadingContainer";
import { PackageMessagePanel } from "Feed/Legacy/Components/PackageMessagePanel";
import { PackageRetentionMessage } from "Feed/Legacy/Components/PackageRetentionMessage";
import { PackageVersionDetails } from "Feed/Legacy/Components/PackageVersionDetails";
import { PackageVersionsList } from "Feed/Legacy/Components/PackageVersionsList";
import { WrappedIvyPromotePanel } from "Feed/Legacy/Components/WrappedIvyPromotePanel";
import { ILegacyControllerViewProps } from "Feed/Legacy/ControllerViews/ILegacyControllerViewProps";
import { IUpstreamBehaviorService } from "Feed/Legacy/DataServices/UpstreamBehaviorService";
import { PromoteDialog } from "Feed/Legacy/Dialogs/PromoteDialog";
import * as CommandGetters from "Feed/Legacy/Helpers/CommandGetters";
import * as PermissionHelper from "Feed/Legacy/Helpers/PermissionHelper";
import { PermanentDeleteCommandHelper } from "Feed/Legacy/Protocols/Common/PermanentDeleteCommandHelper";
import { RestorePackageCommandHelper } from "Feed/Legacy/Protocols/Common/RestorePackageCommandHelper";
import { IFeedProtocolProvider } from "Feed/Legacy/Protocols/ProtocolProvider";
import { PackageState, PackageStore } from "Feed/Legacy/Stores/PackageStore";
import { HubAction, IHubState } from "Feed/Legacy/Types/IHubState";
import { IPackageCommand } from "Feed/Legacy/Types/IPackageCommand";
import { Retention } from "Feed/PackageDetails/Util/Retention";
import {
    GlobalMessageLevel,
    IGlobalDialog,
    IGlobalMessageBanner,
    IGlobalMessagesService
} from "TFS/GlobalBanner/GlobalMessagesService.Types";

interface IPackageControllerViewProps {
    fluxStore: PackageStore;
    isRecycleBin: boolean;
    isPackageDependencySelected: boolean;
}

const Actions = {
    showConnectToFeed: FeedHubActions.connectToFeed
};

class PackageControllerView extends VssComponent<IPackageControllerViewProps & typeof Actions, PackageState> {
    // This is approx 100px LESS than the min-width defined by the Main page container (1024px).
    // See Vssf/WebPlatform/Platform/Presentation/Styles/Layout/_Main.scss
    private static readonly MaxBreadcrumItemWidth = "900px";
    private static readonly UpstreamOverrideToggleKey = "override";

    private _hubViewState: IVssHubViewState;
    private _hub: IHub;
    private _packageCommandsGetter: CommandGetters.PackageCommandsGetter = new CommandGetters.PackageCommandsGetter();
    private _pivotUrls: { [key: string]: IObservableViewStateUrl } = {};
    private _onFilterStateChangeHandler;
    private _viewFilterUpdated: boolean;

    constructor(props: IPackageControllerViewProps & typeof Actions, context?: IVssComponentContext) {
        super(props, context);

        this.state = this.getState();

        this._hubViewState = this.props.isRecycleBin
            ? new VssHubViewState(this.context.pageContext, {
                  pivotNavigationBehavior: HistoryBehavior.none,
                  defaultPivot: PackageDetailsPivot.VERSIONS
              })
            : this.props.isPackageDependencySelected
            ? new VssHubViewState(this.context.pageContext, {
                  pivotNavigationBehavior: HistoryBehavior.none,
                  defaultPivot: PackageDetailsPivot.OVERVIEW
              })
            : new VssHubViewState(this.context.pageContext, { defaultPivot: PackageDetailsPivot.OVERVIEW });

        [PackageDetailsPivot.OVERVIEW, PackageDetailsPivot.VERSIONS].forEach(pivotKey => {
            this._pivotUrls[pivotKey] = this._hubViewState.createObservableUrl({ view: pivotKey });
        });

        this._onFilterStateChangeHandler = this._onFilterStateChange.bind(this);
        this._viewFilterUpdated = false;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.props.fluxStore.addChangedListener(this.triggerRender);
        this._hubViewState.filter.subscribe(this._onFilterStateChangeHandler, FILTER_CHANGE_EVENT);
        this._hubViewState.viewOptions.subscribe(this.onViewOptionsChange, VIEW_OPTIONS_CHANGE_EVENT);
        this.applyUpstreamBehaviorFromServiceAsync();
    }

    public componentWillUnmount(): void {
        this._hubViewState.viewOptions.unsubscribe(this.onViewOptionsChange, VIEW_OPTIONS_CHANGE_EVENT);
        this._hubViewState.filter.unsubscribe(this._onFilterStateChangeHandler, FILTER_CHANGE_EVENT);
        this.props.fluxStore.removeChangedListener(this.triggerRender);
        super.componentWillUnmount();
    }

    public componentDidUpdate(): void {
        this.applyUpstreamBehaviorFromServiceAsync();

        const filterState = this._hubViewState.filter.getState();
        // Remember the view filter that was selected in the package list and apply the same filter in the version list.
        // Only applied to the versions list after versions have been fetched and done only once.
        if (
            this.state.selectedFeed.view &&
            this._hubViewState.selectedPivot.value === PackageDetailsPivot.VERSIONS &&
            (filterState[PackageFilterBarConstants.ViewFilterKey] == null ||
                !filterState[PackageFilterBarConstants.ViewFilterKey].value) &&
            this.props.fluxStore._versionsFetched &&
            !this._viewFilterUpdated
        ) {
            const newState = {};
            newState[PackageFilterBarConstants.ViewFilterKey] = { value: ["@" + this.state.selectedFeed.view.name] };
            this._hubViewState.filter.setState(newState);
            this._viewFilterUpdated = true;
        }

        if (this.props.isPackageDependencySelected) {
            this._hubViewState.selectedPivot.value = PackageDetailsPivot.VERSIONS;
        }
    }

    public render(): JSX.Element {
        const { selectedFeed, selectedPackage, selectedVersion, selectedVersions, feedViews, isLoading } = this.state;
        // There is a period where we have switched to the package pane but we have not looked up the package by id yet.
        // Only display the package if we have a package to view.
        if (this._packageVersionIsAvailable(selectedPackage, selectedVersion) === false) {
            return null;
        }

        const versions = this.props.isRecycleBin
            ? this.props.fluxStore.getRecycleBinPackageVersions()
            : this.props.fluxStore.getPackageVersions();

        const retentionVersionCount = this.getPackageVersionCount(this.state);
        const shouldDisplayRetention = Retention.shouldDisplayRetention(
            this.state.selectedFeed.retentionPolicy,
            retentionVersionCount
        );
        const retentionMessageDismissed = this.state.retentionMessageSuppressions[selectedPackage.id] != null;
        const message = this._getPackageMessagePanelMessage();

        return (
            <div className="packages-controllerview package-controller-view relative">
                {/* hidden h1 for accessibility */}
                <h1 className="visibly-hidden">{this.getHeadingName()}</h1>
                {!retentionMessageDismissed && (
                    <PackageRetentionMessage
                        pageContext={this.context.pageContext}
                        packageId={selectedPackage.id}
                        versionCount={retentionVersionCount}
                        shouldDisplayRetention={shouldDisplayRetention}
                    />
                )}
                <PackageMessagePanel message={message} />
                <Hub
                    className={
                        "packages-hubcontent absolute-fill " +
                        this.getOffSetClassName(message, retentionMessageDismissed, shouldDisplayRetention)
                    }
                    componentRef={hub => (this._hub = hub)}
                    hubViewState={this._hubViewState}
                    commands={this.getCommonCommands()}
                    viewActions={this.getViewActions()}
                    onRenderFilterBar={this._getFilterBar}
                    pivotHeaderAriaLabel={PackageResources.PackagePivot_AriaLabel}
                >
                    <HubHeader
                        breadcrumbItems={this.getHeaderBreadcrumbItems()}
                        maxBreadcrumbItemWidth={PackageControllerView.MaxBreadcrumItemWidth}
                        hubBreadcrumbAriaLabel={PackageResources.PackageHub_BreadCrumb_AriaLabel}
                    />
                    {this.props.isRecycleBin === false && (
                        <PivotBarItem
                            className="packages-pivotBarItem"
                            name={PackageResources.PackageVersionsPivotTabTitle_Overview}
                            itemKey={PackageDetailsPivot.OVERVIEW}
                            commands={this._getPackageCommands(false /*versions pivot*/)}
                            url={this._pivotUrls[PackageDetailsPivot.OVERVIEW]}
                        >
                            <LoadingContainer isLoading={isLoading}>
                                <PackageVersionDetails
                                    feed={selectedFeed}
                                    feedViews={feedViews}
                                    selectedPackage={selectedPackage}
                                    packageVersion={selectedVersion}
                                    protocolMap={this.state.protocolMap}
                                    packageFollowState={this.state.packageFollowState}
                                    isPackageModifiedLoading={this.state.isPackageModifiedLoading}
                                    packageVersionMetrics={
                                        this.state.packageVersionMetricsMap != null
                                            ? this.state.packageVersionMetricsMap[selectedVersion.id]
                                            : null
                                    }
                                    packageMetrics={this.state.packageMetrics}
                                    isSmartDependenciesEnabled={this.state.smartDependenciesEnabled}
                                    isProvenanceEnabled={this.state.provenanceEnabled}
                                    isMetricsLoading={this.state.isMetricsLoading || this.state.isVersionMetricsLoading}
                                    isMetricsEnabled={this.state.isMetricsEnabled}
                                />
                            </LoadingContainer>
                        </PivotBarItem>
                    )}
                    <PivotBarItem
                        className="packages-pivotBarItem"
                        name={PackageResources.PackageVersionsPivotTabTitle_Versions}
                        itemKey={PackageDetailsPivot.VERSIONS}
                        url={this._pivotUrls[PackageDetailsPivot.VERSIONS]}
                        commands={this._getPackageCommands(true /*versions pivot*/)}
                    >
                        <PackageVersionsList
                            pageContext={this.context.pageContext}
                            selectedPackage={selectedPackage}
                            feed={selectedFeed}
                            selectedVersion={selectedVersion}
                            items={versions}
                            protocolMap={this.state.protocolMap}
                            displayVersionDetails={this._onDisplayVersionDetails}
                            selectedFeed={selectedFeed}
                            filterState={this._hubViewState.filter as Filter}
                            isRecycleBin={this.props.isRecycleBin}
                            actions={this._getActionsMap()}
                            clearSelection={!selectedVersions || selectedVersions.length === 0}
                            isLoading={
                                isLoading ||
                                (this.props.isRecycleBin === false &&
                                    this.props.fluxStore._versionsFetched === false) ||
                                (this.props.isRecycleBin === true &&
                                    this.props.fluxStore._recycleBinVersionsFetched === false)
                            }
                            metricsEnabled={this.state.isMetricsEnabled}
                            packageVersionMetricsMap={this.state.packageVersionMetricsMap}
                        />
                    </PivotBarItem>
                </Hub>
                {this.state.showCreateBadgePanel && (
                    <CreateBadgePanel
                        pageContext={this.context.pageContext}
                        feed={selectedFeed}
                        selectedPackage={selectedPackage}
                        views={feedViews}
                    />
                )}
                {this.state.showPromoteDialog && (
                    <PromoteDialog
                        feed={selectedFeed}
                        selectedPackage={selectedPackage}
                        selectedVersion={
                            selectedVersions && selectedVersions.length === 1 /*Currently only for single promote*/
                                ? selectedVersions[0]
                                : selectedVersion
                        }
                        views={feedViews}
                        isLoading={isLoading}
                    />
                )}
                <WrappedIvyPromotePanel />
            </div>
        );
    }

    private getOffSetClassName(
        generalMessage: string,
        retentionMessageDismissed: boolean,
        shouldDisplayRetention: boolean
    ): string {
        const generalMessageBarDisplayed = generalMessage && generalMessage.length > 0;
        const retentionMessageDisplayed = !retentionMessageDismissed && shouldDisplayRetention;

        if (generalMessageBarDisplayed && !retentionMessageDisplayed) {
            return " offSetMessageBarHeight";
        } else if (!generalMessageBarDisplayed && retentionMessageDisplayed) {
            return " offSetMessageBarHeight";
        } else if (generalMessageBarDisplayed && retentionMessageDisplayed) {
            return " offSetGeneralAndRetentionMessageBarHeight";
        }
        return "";
    }

    private get packageProtocol() {
        return this.state.protocolMap[this.state.selectedPackage.protocolType];
    }

    private getPackageVersionCount(state: Readonly<PackageState>): number {
        if (this.props.isRecycleBin) {
            return 0;
        }

        const pkgVersions =
            state.selectedPackage && state.selectedPackage.versions
                ? state.selectedPackage.versions.filter(v => !v.isDeleted).length
                : 0;
        const versions =
            state.selectedVersion && state.selectedVersion.otherVersions
                ? state.selectedVersion.otherVersions.filter(v => !v.isDeleted).length
                : 0;

        return Math.max(pkgVersions, versions);
    }

    private _getActionsMap(): { [key: string]: Action<{}> } {
        if (this.props.isRecycleBin === true) {
            return {
                VersionsPivotSelected: RecycleBinActions.VersionsPivotSelected
            };
        }

        return {
            PackageVersionSelected: FluxActions.PackageVersionSelected,
            VersionsPivotSelected: FluxActions.VersionsPivotSelected
        };
    }

    private _getPackageMessagePanelMessage(): string {
        if (this.props.isRecycleBin === true) {
            return "";
        }

        return this.packageProtocol.getPackageMessage(this.state.selectedPackage, this.state.selectedVersion);
    }

    private _onFilterStateChange(): void {
        FluxActions.PackageVersionsFiltersChanged.invoke(this._hubViewState.filter.getState());
    }

    private _getFilterBar = (): React.ReactElement<IFilterBarProps> => {
        if (this.props.isRecycleBin === true) {
            return null;
        }

        if (this._hubViewState.selectedPivot.value === PackageDetailsPivot.VERSIONS) {
            // tslint:disable-next-line:no-bitwise
            const supportsPromote = (this.packageProtocol.supportedCommandsMask & ProtocolCommands.Promote) !== 0;

            return (
                <FilterBar filter={this._hubViewState.filter} className="packages-filterbar">
                    <KeywordFilterBarItem
                        key={PackageFilterBarConstants.KeywordFilterKey}
                        filterItemKey={PackageFilterBarConstants.KeywordFilterKey}
                        placeholder={PackageResources.VersionList_FilterPlaceHolder}
                    />
                    {supportsPromote && (
                        <PickListFilterBarItem
                            filterItemKey={PackageFilterBarConstants.ViewFilterKey}
                            placeholder={PackageResources.FeedViewDropdown_Title}
                            className="view-dropdown"
                            disabled={this.state.selectedFeed.view ? true : false}
                            getPickListItems={() => this.state.feedViews.map(view => "@" + view.name)}
                        />
                    )}
                </FilterBar>
            );
        }

        return null;
    };

    private _packageVersionIsAvailable(pkg: Package, version: PackageVersion): boolean {
        if (pkg && version) {
            const urlState: IHubState = this.context.pageContext
                .getService<IVssHistoryService>("IVssHistoryService")
                .getState() as IHubState;
            return urlState.package === pkg.name;
        }

        return false;
    }

    // TODO: See if we can rework the navigation changes to avoid having to explicitly update the selected pivot
    private _onDisplayVersionDetails = (isChangingVersion: boolean): void => {
        const behavior = isChangingVersion ? HistoryBehavior.replace : HistoryBehavior.newEntry;
        this._hubViewState.updateNavigationState(behavior, () => {
            this._hubViewState.selectedPivot.value = null;
        });
    };

    public getStore() {
        return this.props.fluxStore;
    }

    public getState() {
        return this.getStore().getPackageState();
    }

    private _getPackageCommands(versionsPivot?: boolean): IPivotBarAction[] {
        const { selectedFeed, selectedPackage, selectedVersion, selectedVersions, protocolMap } = this.state;

        const packageProtocol = protocolMap[selectedPackage.protocolType];
        let packageCommands: IPackageCommand[] = [];

        if (this.props.isRecycleBin === true) {
            return this.getRecycleBinCommands();
        }

        if (versionsPivot) {
            if (!selectedVersions || selectedVersions.length === 0) {
                return [];
            } else if (selectedVersions.length === 1) {
                packageCommands = this._packageCommandsGetter.getSingleSelectionItems(
                    selectedFeed,
                    packageProtocol,
                    selectedPackage,
                    selectedVersions[0],
                    false /*viaPackageList*/,
                    this.context.pageContext
                );
            } else {
                packageCommands = protocolMap[selectedPackage.protocolType].getMultiSelectPackageCommands(
                    selectedFeed,
                    null,
                    selectedVersions
                );
            }
        } else {
            packageCommands = this._packageCommandsGetter.getSingleSelectionItems(
                selectedFeed,
                packageProtocol,
                selectedPackage,
                selectedVersion,
                /*viaPackageList*/ false,
                this.context.pageContext
            );
            packageCommands.push(this._packageCommandsGetter.getFollowItem(this.state.packageFollowState));
        }

        return this.toPivotBarActions(packageCommands);
    }

    private toPivotBarActions(packageCommands: IPackageCommand[]): IPivotBarAction[] {
        let actions = null;
        if (packageCommands && packageCommands.length > 0) {
            actions = packageCommands.map(cmd => {
                return {
                    key: cmd.id,
                    important: true, // whilst important, it doesn't guarantee that the commands won't be moved to the overflow menu
                    name: cmd.displayText,
                    title: cmd.titleText,
                    iconProps: { iconName: cmd.icon } as IVssIconProps,
                    onClick: () => {
                        cmd.actionMethod.apply(this);
                    },
                    disabled: cmd.disabled
                } as IPivotBarAction;
            });
        }

        return actions;
    }

    private getCommonCommands(): IPivotBarAction[] {
        if (this.props.isRecycleBin === true) {
            return [];
        }

        const newConnectToFeedUi = isFeatureFlagEnabled(
            this.context.pageContext,
            "Packaging.Feed.ConnectToFeed.EnableUI"
        );

        const connectToFeedCommand = {
            key: "connect",
            name: PackageResources.ConnectToFeedButtonText,
            important: true,
            iconProps: { iconName: "plug-outline", iconType: VssIconType.bowtie },
            onClick: (ev, action) => {
                newConnectToFeedUi ? this.props.showConnectToFeed() : this._showConnectToFeedDialog();
            }
        } as IPivotBarAction;

        return [connectToFeedCommand];
    }

    private getViewActions(): IPivotBarViewAction[] {
        const actions: IPivotBarViewAction[] = [];

        const upstreamOverride = this.getUpstreamOverrideViewAction();
        if (upstreamOverride) {
            actions.push(upstreamOverride);
        }

        return actions;
    }

    private getUpstreamOverrideViewAction(): IPivotBarViewAction {
        const { selectedFeed, selectedPackage } = this.state;
        if (!selectedFeed || !selectedPackage) {
            return null;
        }

        const protocolType = selectedPackage.protocolType.toLowerCase();
        const pageContext = this.context.pageContext;

        // Is there at least one upstream for the package's protocol
        const upstreams = selectedFeed.upstreamSources.filter(u => u.protocol.toLowerCase() === protocolType);
        if (upstreams.length) {
            // Are public upsteams supported for the package's protocol
            const service = pageContext.getService<IFeedProtocolProvider>("IFeedProtocolProvider");
            const protocol = service.get(protocolType);
            const supportedFeatures = protocol.supportedFeatures(selectedFeed);
            const role = PermissionHelper.getUsersRoleForFromFeed(selectedFeed);
            const disabled = !supportedFeatures.publicUpstreams || !PermissionHelper.isAdministratorFromRole(role);

            return {
                key: PackageControllerView.UpstreamOverrideToggleKey,
                name: PackageResources.UpstreamBehaviorOverride,
                actionType: PivotBarViewActionType.OnOff,
                title: PackageResources.UpstreamBehaviorOverrideTitle,
                ariaLabel: PackageResources.UpstreamBehaviorOverride,
                disabled
            } as IPivotBarViewAction;
        }
    }

    private getRecycleBinCommands(): IPivotBarAction[] {
        const state = this.state;

        if (!state.selectedVersions) {
            return [];
        }

        let packageCommands: IPackageCommand[] = [];

        const role: FeedRole = PermissionHelper.getUsersRoleForFromFeed(state.selectedFeed);

        if (!PermissionHelper.isAdministratorFromRole(role)) {
            return this.toPivotBarActions([]);
        }

        const restoreToFeedCommands = RestorePackageCommandHelper.getCommands(
            state.selectedPackage.protocolType,
            state.selectedPackage,
            state.selectedVersions,
            state.selectedPackage.name
        );
        packageCommands = packageCommands.concat(restoreToFeedCommands);

        const permanentDeleteCommands = PermanentDeleteCommandHelper.getCommands(
            state.selectedPackage.protocolType,
            null,
            state.selectedVersions,
            state.selectedPackage.name
        );
        packageCommands = packageCommands.concat(permanentDeleteCommands);

        return this.toPivotBarActions(packageCommands);
    }

    private _showConnectToFeedDialog(): void {
        const state = this.state;
        const selectedPackage = state.selectedPackage;
        const protocolKey = selectedPackage.protocolType;
        const currentProtocolMap = {
            [protocolKey]: state.protocolMap[selectedPackage.protocolType]
        };
        OpenConnectToFeedDialog(
            this.context.pageContext,
            state.selectedFeed,
            state.feedViews,
            currentProtocolMap,
            HubAction.Feed
        );
    }

    private getHeaderBreadcrumbItems(): IHubBreadcrumbItem[] {
        const state = this.state;
        const items: IHubBreadcrumbItem[] = [];

        if (state.selectedFeed) {
            items.push({
                key: "feed",
                text: state.selectedFeed.name,
                onClick: () => FluxActions.FeedBreadcrumbSelected.invoke(state.selectedPackage)
            });
        }

        if (this.props.isRecycleBin) {
            items.push({
                key: "recycleBin",
                text: PackageResources.RecycleBin_Breadcrumb,
                onClick: () => RecycleBinActions.RecycleBinBreadCrumbClicked.invoke({})
            });
        }

        if (state.selectedPackage && state.selectedVersion) {
            const breadCrumbPackageText = this.getHeadingName();

            items.push({
                key: "package",
                text: breadCrumbPackageText,
                leftIconProps: this.getIconProps(state.selectedPackage.protocolType)
            });
        }

        return items;
    }

    private getHeadingName(): string {
        return this.props.isRecycleBin
            ? `${this.state.selectedPackage.name}`
            : `${this.state.selectedPackage.name} ${this.state.selectedVersion.version}`;
    }

    private getIconProps(protocolType: string): IVssIconProps {
        let iconProps: IVssIconProps = null;
        const packageProtocol = this.state.protocolMap[protocolType];
        if (packageProtocol) {
            iconProps = packageProtocol.vssIconProps;
        }

        return iconProps;
    }

    private triggerRender = () => this.setState(this.getState());

    private async applyUpstreamBehaviorFromServiceAsync(): Promise<void> {
        const { selectedFeed, selectedPackage } = this.state;
        if (selectedFeed && selectedPackage) {
            try {
                const service = this.context.pageContext.getService<IUpstreamBehaviorService>(
                    "IUpstreamBehaviorService"
                );

                const behavior = await service.getUpstreamBehaviorAsync(selectedFeed, selectedPackage);
                const allowExternals =
                    behavior.versionsFromExternalUpstreams == UpstreamVersionVisibility.AllowExternalVersions;

                const viewOptions = this._hubViewState.viewOptions.getViewOptions();
                viewOptions[PackageControllerView.UpstreamOverrideToggleKey] = allowExternals;
                this._hubViewState.viewOptions.setViewOptions(viewOptions, true /* supressChangeEvent */);
            } catch {
                // Do nothing - especially not blow up the UI.
            }
        }
    }

    private onViewOptionsChange = (values: IViewOptionsValues, action?: string): void => {
        if (values.hasOwnProperty(PackageControllerView.UpstreamOverrideToggleKey)) {
            this.saveUpstreamingBehaviorAsync(values[PackageControllerView.UpstreamOverrideToggleKey]);
        }
    };

    private async saveUpstreamingBehaviorAsync(allowExternalVersions: boolean): Promise<void> {
        const behavior = {
            versionsFromExternalUpstreams: allowExternalVersions
                ? UpstreamVersionVisibility.AllowExternalVersions
                : UpstreamVersionVisibility.Auto
        } as UpstreamingBehavior;

        const pageContext = this.context.pageContext;
        const upstreamService = pageContext.getService<IUpstreamBehaviorService>("IUpstreamBehaviorService");
        const messageService = pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");

        try {
            await upstreamService.setUpstreamBehaviorAsync(
                this.state.selectedFeed,
                this.state.selectedPackage,
                behavior
            );

            if (allowExternalVersions) {
                messageService.addDialog({
                    componentType: "dialog",
                    contentProperties: {
                        titleProps: { text: PackageResources.UpstreamBehaviorOverride },
                        showCloseButton: true,
                        children: (
                            <FormatComponent className="message" format={PackageResources.UpstreamBehaviorAlert}>
                                <Link
                                    href="https://go.microsoft.com/fwlink/?linkid=2152379"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {PackageResources.LearnMore}
                                </Link>
                            </FormatComponent>
                        ),
                        onDismiss: () => {
                            messageService.closeDialog();
                            /** This is the best way I could come up with to select the override button. We can't set the button's class,
                             * id, or other commonly used method of selection because of the inflexibility of the <Hub/> component. Other
                             * selection strategies (including the button's class name assigned by the hub component) rely on the index 
                             * of the button relative to other buttons on the command bar. Selecting the button that has a popup seems 
                             * like the least fragile way to do this (but still terrible) */
                            const externalVersionsButton = document.querySelector(".packages-hubcontent .ms-CommandBar-sideCommands button[aria-haspopup=true]") as HTMLElement;
                            externalVersionsButton?.focus();
                        }
                    },
                } as IGlobalDialog);
            }
        } catch (error) {
            // Most likely error is 403 (not a feed owner)
            const message = StringUtils.format(
                PackageResources.UpstreamBehaviorOverrideSaveFailure,
                error && error.message
            );
            messageService.addBanner({
                dismissable: true,
                level: GlobalMessageLevel.error,
                message
            } as IGlobalMessageBanner);
            return;
        }
    }
}

const PackageDetailsView = connect<IPackageControllerViewProps, typeof Actions, ILegacyControllerViewProps>(
    (store, ownProps: ILegacyControllerViewProps) => ({
        fluxStore: ownProps.packageFluxStore,
        isRecycleBin: false,
        isPackageDependencySelected: false
    }),
    Actions
)(PackageControllerView);

const RecycleBinPackageView = connect(
    (store: {}, ownProps: ILegacyControllerViewProps) => ({
        fluxStore: ownProps.packageFluxStore,
        isRecycleBin: true,
        isPackageDependencySelected: false
    }),
    Actions
)(PackageControllerView);

const PackageDependencyView = connect(
    (store: {}, ownProps: ILegacyControllerViewProps) => ({
        fluxStore: ownProps.packageFluxStore,
        isRecycleBin: false,
        isPackageDependencySelected: true
    }),
    Actions
)(PackageControllerView);

VssComponent.register("packagedetails", PackageDetailsView);
VssComponent.register("recyclebinpackage", RecycleBinPackageView);
VssComponent.register("packagedependency", PackageDependencyView);
